<template>
  <div class="slideshow-audio">
    <div ref="visualizer" class="visualizer" :class="{ 'recently-change': recentlyChanged }">
      <span v-for="i in 48" :key="i" ref="bars" />
    </div>

    <audio
      v-if="url != null"
      ref="audio"
      :src="url"
      :autoplay="autoplay"
      :class="{ 'recently-change': recentlyChanged }"
      loop
      :controls="controls"
      crossorigin="anonymous"
      @loadedmetadata="onLoadedMetadata"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, useTemplateRef, watch } from "vue";
import { useMinioSignedUrl } from "@/functions/useMinioSignedUrl";

const props = defineProps({
  fileUUID: {
    type: String,
    required: true,
  },
  autoplay: {
    type: Boolean,
    required: false,
    default: false,
  },
  recentlyChanged: {
    type: Boolean,
    required: false,
    default: false,
  },
  controls: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const { url } = useMinioSignedUrl(computed(() => props.fileUUID));

const audio = useTemplateRef<HTMLAudioElement>("audio");
const bars = useTemplateRef<HTMLElement[]>("bars");

const visualizer = useTemplateRef("visualizer");

const isIdle = ref(true);

let ctx: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let source: MediaElementAudioSourceNode | null = null;
let dataArray: Uint8Array<ArrayBuffer> | null = null;
let animationFrameId: number | null = null;
let attachedAudio: HTMLAudioElement | null = null;

function stopAnimationLoop() {
  if (animationFrameId != null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
}

function resetBars() {
  if (bars.value == null) return;

  bars.value.forEach((bar) => {
    bar.style.height = "";
    bar.style.opacity = "";
  });
}

function animate() {
  if (analyser == null || dataArray == null || bars.value == null || visualizer.value == null) {
    animationFrameId = null;
    return;
  }

  analyser.getByteFrequencyData(dataArray);

  const styles = window.getComputedStyle(visualizer.value);
  const paddingTop = Number.parseFloat(styles.paddingTop) || 0;
  const paddingBottom = Number.parseFloat(styles.paddingBottom) || 0;
  const maxHeight = visualizer.value.clientHeight - paddingTop - paddingBottom - 8;

  bars.value.forEach((bar, i) => {
    const value = dataArray?.[i] ?? 0;
    const height = Math.max(16, (value / 255) * maxHeight);

    bar.style.height = `${height}px`;
    bar.style.opacity = `${0.35 + (value / 255) * 0.65}`;
  });

  animationFrameId = requestAnimationFrame(animate);
}

async function handlePlay() {
  if (audio.value == null || ctx == null) return;

  if (ctx.state === "suspended") {
    await ctx.resume();
  }

  isIdle.value = false;

  if (animationFrameId == null) {
    animate();
  }
}

function handlePause() {
  stopAnimationLoop();
  isIdle.value = true;
  resetBars();
}

function detachAudio() {
  if (attachedAudio != null) {
    attachedAudio.removeEventListener("play", handlePlay);
    attachedAudio.removeEventListener("playing", handlePlay);
    attachedAudio.removeEventListener("pause", handlePause);
    attachedAudio.removeEventListener("ended", handlePause);
  }

  attachedAudio = null;
  source?.disconnect();
  source = null;
}

async function attachAudio() {
  await nextTick();

  if (audio.value == null || attachedAudio === audio.value) return;

  detachAudio();

  if (ctx == null) {
    ctx = new AudioContext();
    analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.8;
    dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.connect(ctx.destination);
  }

  if (analyser == null) return;

  source = ctx.createMediaElementSource(audio.value);
  source.connect(analyser);

  audio.value.addEventListener("play", handlePlay);
  audio.value.addEventListener("playing", handlePlay);
  audio.value.addEventListener("pause", handlePause);
  audio.value.addEventListener("ended", handlePause);
  attachedAudio = audio.value;
}

watch(url, () => void attachAudio(), { immediate: true });

onUnmounted(() => {
  detachAudio();
  stopAnimationLoop();
  analyser?.disconnect();

  if (ctx != null && ctx.state !== "closed") {
    void ctx.close();
  }
});

const emit = defineEmits<{
  loadedmetadata: [durationInSeconds: number];
}>();

function onLoadedMetadata() {
  const duration = audio.value?.duration;

  if (duration != null && Number.isFinite(duration) && duration > 0) {
    emit("loadedmetadata", duration);
  }
}
</script>

<style scoped lang="scss">
@use "@/modules/filemanagement/components/FMFileViewer/FMFileViewer.scss" as *;

.slideshow-audio {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  width: 100%;
  height: 100%;
  padding: clamp(24px, 4vw, 48px);
  box-sizing: border-box;
}

.visualizer {
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: clamp(4px, 0.6vw, 10px);
  width: min(90vw, 1200px, 100%);
  height: clamp(180px, 35vh, 420px);
  padding: clamp(20px, 3vw, 36px);
  box-sizing: border-box;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
}

.visualizer span {
  display: block;
  flex: 1 1 0;
  min-width: 8px;
  max-width: 24px;
  height: 10px;
  border-radius: 999px;
  background: linear-gradient(
    to top,
    var(--p-success-color),
    var(--p-warn-color),
    var(--p-danger-color)
  );
  transform-origin: bottom;
}
</style>
